import React, { useState, useEffect } from 'react';
import { Award, Plus, Search, Edit, Eye, CheckCircle, XCircle, ArrowUpDown, ArrowUp, ArrowDown, Filter, X, Home } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { firebaseService } from '../services/firebaseService';
import { useNotification } from '../hooks/useNotification';
import NivelModal from '../components/NivelModal';

interface Requisito {
  id: string;
  nome: string;
  nivelId?: string;
  createDate?: string;
  updateDate?: string;
  deleteDate?: string;
  ordem?: number;
}

interface Nivel {
  id: string;
  nome: string;
  descricao?: string;
  cor?: string;
  ordenacao?: number;
  ordem?: number; // Mantido para compatibilidade
  ativo: boolean;
  createDate?: string;
  updateDate?: string;
  imagem?: string;
  requisitos?: { [key: string]: Requisito } | Requisito[];
}

const Niveis: React.FC = () => {
  const { userProfile } = useAuth();
  const { showSuccess, showError, showConfirm, showInfo, NotificationComponent } = useNotification();
  const [niveis, setNiveis] = useState<Nivel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'nome' | 'ordem' | 'status'>('ordem');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedNivel, setSelectedNivel] = useState<Nivel | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showNivelModal, setShowNivelModal] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Carregar níveis do Firebase
  useEffect(() => {
    const loadNiveis = async () => {
      if (!userProfile?.empresaCnpj) return;

      try {
        setLoading(true);
        setError(null);
        const data = await firebaseService.getNiveis(userProfile.empresaCnpj);
        setNiveis(data);
        console.log('✅ Níveis carregados:', data.length);
      } catch (err) {
        console.error('❌ Erro ao carregar níveis:', err);
        setError('Erro ao carregar níveis');
      } finally {
        setLoading(false);
      }
    };

    loadNiveis();
  }, [userProfile?.empresaCnpj]);

  // Função de ordenação
  const handleSort = (field: 'nome' | 'ordem' | 'status') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Filtrar e ordenar níveis
  const filteredAndSortedNiveis = niveis
    .filter(nivel => {
      const matchesSearch = (nivel.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (nivel.descricao || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'todos' || 
                           (statusFilter === 'ativo' && nivel.ativo) ||
                           (statusFilter === 'inativo' && !nivel.ativo);
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aValue: string | number | boolean;
      let bValue: string | number | boolean;

      switch (sortBy) {
        case 'nome':
          aValue = (a.nome || '').toLowerCase();
          bValue = (b.nome || '').toLowerCase();
          break;
        case 'ordem':
          aValue = a.ordenacao || a.ordem || 0;
          bValue = b.ordenacao || b.ordem || 0;
          break;
        case 'status':
          aValue = a.ativo;
          bValue = b.ativo;
          break;
        default:
          aValue = a.ordenacao || a.ordem || 0;
          bValue = b.ordenacao || b.ordem || 0;
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  // Estatísticas
  const totalNiveis = niveis.length;
  const niveisAtivos = niveis.filter(n => n.ativo).length;
  const niveisInativos = totalNiveis - niveisAtivos;

  // Funções de modal
  const handleViewNivel = (nivel: Nivel) => {
    setSelectedNivel(nivel);
    setShowModal(true);
  };

  const handleEditNivel = (nivel: Nivel) => {
    setSelectedNivel(nivel);
    setShowNivelModal(true);
  };

  const handleNewNivel = () => {
    setSelectedNivel(null);
    setShowNivelModal(true);
  };

  const handleCloseNivelModal = () => {
    setShowNivelModal(false);
    setSelectedNivel(null);
  };

  const handleSaveNivel = async (nivelData: Partial<Nivel>) => {
    if (!userProfile?.empresaCnpj) return;

    try {
      if (nivelData.id) {
        // Atualizar nível existente
        await firebaseService.updateNivel(userProfile.empresaCnpj, nivelData.id, nivelData);
      } else {
        // Criar novo nível
        await firebaseService.createNivel(userProfile.empresaCnpj, nivelData);
      }
      
      // Recarregar dados
      const data = await firebaseService.getNiveis(userProfile.empresaCnpj);
      setNiveis(data);
      
      handleCloseNivelModal();
      showSuccess(
        nivelData.id ? 'Nível atualizado' : 'Nível criado',
        nivelData.id ? 'Os dados do nível foram atualizados com sucesso.' : 'O nível foi cadastrado com sucesso.'
      );
      return Promise.resolve();
    } catch (error) {
      console.error('Erro ao salvar nível:', error);
      showError('Erro ao salvar', 'Não foi possível salvar o nível. Tente novamente.');
      throw error;
    }
  };

  const handleDeleteNivel = async (nivel: Nivel) => {
    showConfirm(
      'Inativar Nível',
      `Tem certeza que deseja inativar o nível "${nivel.nome}"?`,
      async () => {
        try {
          await firebaseService.updateNivel(userProfile!.empresaCnpj, nivel.id, { ativo: false });
          const data = await firebaseService.getNiveis(userProfile!.empresaCnpj);
          setNiveis(data);
          showSuccess('Nível inativado', 'O nível foi inativado com sucesso.');
        } catch (error) {
          console.error('Erro ao inativar nível:', error);
          showError('Erro ao inativar', 'Não foi possível inativar o nível. Tente novamente.');
        }
      }
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error}</div>
        <button onClick={() => window.location.reload()} className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors">
          Tentar novamente
        </button>
      </div>
    );
  }


  return (
    <div className="space-y-4">
      <NotificationComponent />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <span className="text-gray-500 dark:text-gray-400">Níveis</span>
              </li>
            </ol>
          </nav>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">Todos</h1>
        </div>
        <button 
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          onClick={handleNewNivel}
        >
          <Plus className="h-4 w-4" />
          <span>Novo Nível</span>
        </button>
      </div>

      {/* Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-medium">{filteredAndSortedNiveis.length}</span> níveis encontrados de{' '}
          <span className="font-medium">{totalNiveis}</span> níveis cadastrados
          {niveisAtivos > 0 && (
            <> • <span className="font-medium text-green-600">{niveisAtivos}</span> ativos</>
          )}
          {niveisInativos > 0 && (
            <> • <span className="font-medium text-orange-600">{niveisInativos}</span> inativos</>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center gap-x-4">
          <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar níveis..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          >
            <option value="todos">Todos os status</option>
            <option value="ativo">Apenas ativos</option>
            <option value="inativo">Apenas inativos</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Imagem
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('ordem')}
                >
                  <div className="flex items-center gap-1">
                    Ordem
                    {sortBy === 'ordem' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 opacity-50" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('nome')}
                >
                  <div className="flex items-center gap-1">
                    Nível
                    {sortBy === 'nome' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 opacity-50" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Requisitos
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortBy === 'status' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 opacity-50" />
                    )}
                  </div>
                </th>
                <th className="relative px-6 py-3 w-40">
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedNiveis.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Award className="h-12 w-12 text-gray-400 mb-3" />
                      <p className="text-gray-500 dark:text-gray-400 font-medium">Nenhum nível encontrado</p>
                      <p className="text-sm text-gray-400 mt-1">
                        {searchTerm || statusFilter !== 'todos' 
                          ? 'Tente ajustar os filtros de busca.' 
                          : 'Comece adicionando um novo nível.'
                        }
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAndSortedNiveis.map((nivel) => (
                  <tr key={nivel.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {nivel.imagem ? (
                      <div className="relative h-20 w-20 flex items-center justify-center bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                        <img 
                          src={nivel.imagem} 
                          alt={nivel.nome || 'Nível'} 
                          className="max-h-20 max-w-20 rounded-lg object-contain cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => setPreviewImage(nivel.imagem || null)}
                          onError={(e) => {
                            // Fallback para ícone se a imagem não carregar
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const fallback = target.nextElementSibling as HTMLElement;
                            if (fallback) {
                              fallback.style.display = 'flex';
                            }
                          }}
                        />
                        <div 
                          className={`h-20 w-20 rounded-lg bg-primary-100 flex items-center justify-center hidden absolute inset-0`}
                        >
                          <Award className="h-8 w-8 text-primary-600" />
                        </div>
                      </div>
                    ) : (
                      <div className="h-20 w-20 rounded-lg bg-primary-100 flex items-center justify-center">
                        <Award className="h-8 w-8 text-primary-600" />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary-600">
                            {nivel.ordenacao ?? nivel.ordem ?? '?'}
                          </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{nivel.nome || 'Sem nome'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">ID: {nivel.id}</div>
                  </td>
                    <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-gray-100 max-w-xs truncate">
                      {nivel.descricao || 'Sem descrição'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {(() => {
                      const requisitos = nivel.requisitos;
                      if (!requisitos) return <span className="text-sm text-gray-500 dark:text-gray-400">Sem requisitos</span>;
                      
                      // Converter objeto para array se necessário
                      const requisitosArray = Array.isArray(requisitos) 
                        ? requisitos 
                        : Object.values(requisitos);
                      
                      if (requisitosArray.length === 0) {
                        return <span className="text-sm text-gray-500 dark:text-gray-400">Sem requisitos</span>;
                      }
                      
                      return (
                        <div className="space-y-1">
                          {requisitosArray.slice(0, 3).map((req: any, idx: number) => (
                            <div key={req.id || idx} className="text-sm text-gray-900 dark:text-gray-100 flex items-start gap-1">
                              <span className="text-primary-600">•</span>
                              <span className="line-clamp-1">{req.nome || 'Sem nome'}</span>
                            </div>
                          ))}
                          {requisitosArray.length > 3 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              +{requisitosArray.length - 3} mais
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                      nivel.ativo 
                          ? 'bg-green-100 text-green-800 border border-green-200' 
                          : 'bg-red-100 text-red-800 border border-red-200'
                    }`}>
                      {nivel.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-x-2">
                      <button 
                        onClick={() => handleViewNivel(nivel)}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50 transition-colors"
                        title="Ver detalhes"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleEditNivel(nivel)}
                          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                        {nivel.ativo && (
                      <button 
                            onClick={() => handleDeleteNivel(nivel)}
                            className="text-red-600 hover:text-red-900 p-2 rounded hover:bg-red-50 transition-colors"
                        title="Inativar"
                      >
                        <XCircle className="h-4 w-4" />
                      </button>
                        )}
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Detalhes */}
      {showModal && selectedNivel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <Award className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Detalhes do Nível</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">ID: {selectedNivel.id}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
              </div>
              
            <div className="p-6">
              {selectedNivel.imagem && (
                <div className="mb-6 flex justify-center">
                  <div className="h-48 w-48 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-center">
                    <img 
                      src={selectedNivel.imagem} 
                      alt={selectedNivel.nome || 'Nível'} 
                      className="max-h-48 max-w-48 rounded-lg object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome</label>
                    <p className="text-gray-900 dark:text-gray-100">{selectedNivel.nome || 'Sem nome'}</p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ordem</label>
                    <p className="text-gray-900 dark:text-gray-100">{selectedNivel.ordenacao ?? selectedNivel.ordem ?? 'Não definida'}</p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descrição</label>
                    <p className="text-gray-900 dark:text-gray-100">{selectedNivel.descricao || 'Sem descrição'}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    selectedNivel.ativo 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700' 
                        : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-700'
                  }`}>
                    {selectedNivel.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                  </div>
                </div>
              </div>
              
              {/* Requisitos */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Requisitos</h3>
                {(() => {
                  const requisitos = selectedNivel.requisitos;
                  if (!requisitos) {
                    return <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum requisito cadastrado</p>;
                  }
                  
                  const requisitosArray = Array.isArray(requisitos) 
                    ? requisitos 
                    : Object.values(requisitos);
                  
                  if (requisitosArray.length === 0) {
                    return <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum requisito cadastrado</p>;
                  }
                  
                  return (
                    <div className="space-y-2">
                      {requisitosArray.map((req: any, idx: number) => (
                        <div key={req.id || idx} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center text-xs font-semibold">
                            {idx + 1}
                          </span>
                          <p className="text-sm text-gray-900 dark:text-gray-100 flex-1">{req.nome || 'Sem nome'}</p>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              
              <div className="flex justify-end gap-x-3 mt-6 pt-6 border-t border-gray-200">
                <button 
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Fechar
                </button>
                <button 
                  onClick={() => {
                    setShowModal(false);
                    handleEditNivel(selectedNivel);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Editar Nível
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição/Criação */}
      <NivelModal
        nivel={selectedNivel}
        isOpen={showNivelModal}
        onClose={handleCloseNivelModal}
        onSave={handleSaveNivel}
      />

      {/* Modal de Preview da Imagem */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors z-10 shadow-lg"
              title="Fechar"
            >
              <X className="h-6 w-6 text-gray-700" />
            </button>
            <img 
              src={previewImage} 
              alt="Preview" 
              className="w-full h-auto rounded-lg shadow-2xl object-contain max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Niveis;
